import React from "react";
import {
  RegisterOptions,
  useController,
  Control,
  FieldValues,
  Path,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { LinkButtonProps } from "@components/new/LinkButton";
import TextInput, { preparePhoneString } from "../components/TextInput/TextInput.tsx";

interface TextInputContainerProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  title: React.ReactNode;
  placeholder?: string;
  type?: "text" | "password" | "phone" | "email" | "number";
  error?: string;
  hasError?: boolean;
  disabled?: boolean;
  maxLength?: number,
  min?: number,
  disabledMessage?: string,
  onlyCountry?: string | undefined;
  onBlur?: () => void;
  onChange?: (v: string) => void;
  button?: LinkButtonProps;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

const TextInputContainer = <TFieldValues extends FieldValues = FieldValues>(
  props: TextInputContainerProps<TFieldValues>,
) => {
  const { t } = useTranslation();

  const typeRules = {
    email: {
      pattern: {
        value: /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
        message: t("defaultFormErrors.email"),
      },
    },
    phone: {
      validate: (value: string) => (
        isValidPhoneNumber(preparePhoneString(value))
        || t("defaultFormErrors.phone")
      ),
    },
    text: {},
    password: {},
    number: {}
  }

  const { field, fieldState } = useController<TFieldValues>({
    control: props.control,
    name: props.name,
    rules: {
      ...(typeRules[props.type ?? "text"] || {}),
      ...props.rules,
    },
  });

  const maxLength = props.maxLength ?? 50;

  return (
    <TextInput
      value={field.value}
      placeholder={props.placeholder}
      onChange={(v) => {
        field.onChange(v);
        props.onChange && props?.onChange(v);
      }}
      onBlur={() => {
        field.onBlur();
        props.onBlur && props?.onBlur();
      }}
      name={field.name}
      title={props.title}
      errorMessage={(fieldState.error && fieldState.error?.message)|| props.error}
      hasError={Boolean(fieldState.error || props.hasError)}
      type={props.type}
      disabled={props.disabled}
      maxLength={maxLength}
      min={props.min}
      disabledMessage={props.disabledMessage}
      onlyCountry={props.onlyCountry}
      required={Boolean(props.rules?.required)}
      button={props.button}
      prefix={props.prefix}
      suffix={props.suffix}
    />
  );
};

export default TextInputContainer;
